import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { ArrowLeft, Plus, Trash2, Loader2, Sprout, Copy, CheckCircle2, ListOrdered } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import { API_BASE } from "./constants";
import { SearchableSelect } from "./SearchableSelect";
import AddToItineraryButton from "./AddToItinerary";
import { useCultureLookup } from "@/lib/cultures";

import { Field, ProductInfoPanel, splitDose, pickRecommendedDose } from "./shared";
import BilanFertilisation, { UnitesApport, TeneursFields, teneursFromName, DensiteField, BouillieField, isVolumeUnit, isConcUnit } from "./BilanFertilisation";

import { UNITS, isEphy, fertiProducts, productItems as buildProductItems } from "./fertiProducts";
import { FicheFertiPanel, FicheFertiForm } from "./FicheFerti";
export default function FertilisationTab({ parcelId, refData, surfaceHa, cultureType, ilotId, ilots = [], onOpenItineraries }) {
  const ilotName = (id) => (ilots.find((i) => i.ilot_id === id) || {}).name;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [duplicateFrom, setDuplicateFrom] = useState(null);
  // Fiche ferti : null = fermée, {} = nouvelle, sinon la fiche à modifier
  const [ficheEdit, setFicheEdit] = useState(null);
  const [ficheReload, setFicheReload] = useState(0);
  const [currentFiche, setCurrentFiche] = useState(null);
  // Bilan depuis une date choisie (1er janvier par défaut), gardée par parcelle dans ce navigateur
  const depuisKey = `bilanFertiDepuis:${parcelId}`;
  const [depuis, setDepuisState] = useState(() => {
    try { return localStorage.getItem(depuisKey) || `${new Date().getFullYear()}-01-01`; } catch { return `${new Date().getFullYear()}-01-01`; }
  });
  const setDepuis = (d) => { setDepuisState(d); try { localStorage.setItem(depuisKey, d); } catch { /* stockage indisponible */ } };
  const [bilan, setBilan] = useState(null);
  const [bilanLoading, setBilanLoading] = useState(false);
  const bilanReq = useRef(0); // seule la réponse de la dernière demande s'affiche

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/carnet/fertilisations?parcel_id=${parcelId}${ilotId ? `&ilot_id=${ilotId}` : ""}`);
      if (res.ok) setItems(await res.json());
    } catch (err) { console.error('[FertilisationTab] fetch failed:', err); }
    setLoading(false);
  }, [parcelId, ilotId]);

  const fetchBilan = useCallback(async () => {
    const req = ++bilanReq.current;
    setBilanLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/carnet/bilan-fertilisation?parcel_id=${parcelId}${ilotId ? `&ilot_id=${ilotId}` : ""}&depuis=${depuis}`);
      const data = res.ok ? await res.json() : null;
      if (req !== bilanReq.current) return;
      if (data) setBilan(data);
    } catch (err) { console.error('[FertilisationTab] bilan failed:', err); }
    if (req === bilanReq.current) setBilanLoading(false);
  }, [parcelId, ilotId, depuis]);

  useEffect(() => { fetchData(); }, [fetchData]);
  // Petite attente : la saisie d'une date au clavier passe par des années intermédiaires (0002, 0020…)
  useEffect(() => { const t = setTimeout(fetchBilan, 300); return () => clearTimeout(t); }, [fetchBilan, items]);

  const handleDelete = async (id) => {
    try {
      await authFetch(`${API_BASE}/carnet/fertilisations/${id}`, { method: "DELETE" });
      toast.success("Supprime"); setDeletingId(null); fetchData();
    } catch { toast.error("Erreur"); setDeletingId(null); }
  };

  if (ficheEdit) {
    return <FicheFertiForm parcelId={parcelId} ilotId={ilotId} cultureType={cultureType} refData={refData} fiche={ficheEdit}
      onSaved={() => { setFicheEdit(null); setFicheReload((n) => n + 1); }} onCancel={() => setFicheEdit(null)} />;
  }

  if (showForm) {
    return <FertilisationForm parcelId={parcelId} ilotId={ilotId} refData={refData} surfaceHa={surfaceHa} cultureType={cultureType}
      initial={duplicateFrom}
      onSaved={() => { setShowForm(false); setDuplicateFrom(null); fetchData(); }}
      onCancel={() => { setShowForm(false); setDuplicateFrom(null); }} />;
  }

  const confirmCopy = async (id) => {
    try {
      const res = await authFetch(`${API_BASE}/carnet/duplicate/confirm/fertilisation/${id}`, { method: "POST" });
      if (res.ok) { toast.success("Copie confirmée"); fetchData(); } else toast.error("Confirmation impossible");
    } catch { toast.error("Erreur de connexion"); }
  };


  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-black/5">
        <Button size="sm" className="w-full h-9 text-[11px] font-semibold rounded-lg text-white gap-1.5"
          style={{ background: "#8B5E3C" }}
          onClick={() => { setDuplicateFrom(null); setShowForm(true); }} data-testid="fert-new-btn">
          <Plus className="w-3.5 h-3.5" /> Nouvel apport
        </Button>
        {onOpenItineraries && (
          <Button size="sm" variant="outline" className="w-full h-8 mt-2 text-[11px] font-semibold rounded-lg gap-1.5"
            onClick={onOpenItineraries} data-testid="fert-itineraries-btn">
            <ListOrdered className="w-3.5 h-3.5" /> Itinéraire technique
          </Button>
        )}
      </div>
      <ScrollArea className="flex-1">
        <FicheFertiPanel parcelId={parcelId} ilotId={ilotId} onEdit={setFicheEdit} reloadKey={`${ficheReload}-${items.length}`}
          onRealise={fetchData} onFiche={setCurrentFiche} />
        {/* Avec une fiche ferti, son bilan du cycle remplace le bilan « depuis le » */}
        {!currentFiche && <BilanFertilisation bilan={bilan} depuis={depuis} onDepuis={setDepuis} loading={bilanLoading} onCompleted={fetchBilan} />}
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#8B5E3C" }} /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 px-4">
            <Sprout className="w-8 h-8 mx-auto mb-3 opacity-20" />
            <p className="text-xs" style={{ color: "#7A7A7A" }}>Aucun amendement ou fertilisation</p>
          </div>
        ) : (
          <div className="p-3 space-y-2">
            {items.map((item, idx) => (
              <div key={item.fertilisation_id || idx} className="p-3 rounded-xl border border-black/5" data-testid={`fert-${item.fertilisation_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <Badge className="text-[9px] border-0 px-1.5 py-0" style={{ background: item.category === "amendement" ? "#8B5E3C15" : "#2D6A4F15", color: item.category === "amendement" ? "#8B5E3C" : "#2D6A4F" }}>
                        {item.category === "amendement" ? "Amendement" : "Engrais"}
                      </Badge>
                      <span className="text-[10px]" style={{ color: "#AAAAAA" }}>{item.date}</span>
                      {item.ilot_id && ilotName(item.ilot_id) && <Badge className="text-[9px] border-0 px-1.5 py-0" style={{ background: "#dbe8f1", color: "#2f6a92" }}>{ilotName(item.ilot_id)}</Badge>}
                      {item.status === "a_verifier" && <Badge className="text-[9px] border-0 px-1.5 py-0" style={{ background: "#FBF1DC", color: "#9A6A12" }}>{item.itinerary ? `Itinéraire ${item.itinerary.step}/${item.itinerary.steps} · à vérifier` : "Copie à vérifier"}</Badge>}
                    </div>
                    <p className="text-xs font-semibold mt-1" style={{ color: "#1A1A1A" }}>{item.product_name}</p>
                    {item.composition?.formule && <p className="text-[10px]" style={{ color: "#2D6A4F" }}>{item.composition.formule}</p>}
                    {item.dose && <p className="text-[11px]" style={{ color: "#4A4A4A" }}>{item.dose} {item.unit}</p>}
                    <UnitesApport info={bilan?.apports?.[item.fertilisation_id]} onCompleted={fetchBilan} />
                    {item.method && <p className="text-[10px]" style={{ color: "#7A7A7A" }}>{item.method}</p>}
                    {item.notes && <p className="text-[10px] mt-1 italic" style={{ color: "#7A7A7A" }}>{item.notes}</p>}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {item.status === "a_verifier" && (
                        <Button size="sm" className="h-6 text-[10px] gap-1 rounded-lg text-white" style={{ background: "#2D6A4F" }}
                          onClick={() => confirmCopy(item.fertilisation_id)} data-testid={`fert-confirm-${item.fertilisation_id}`}>
                          <CheckCircle2 className="w-3 h-3" /> Confirmer
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1 rounded-lg"
                        onClick={() => { setDuplicateFrom(item); setShowForm(true); }} data-testid={`fert-duplicate-${item.fertilisation_id}`}>
                        <Copy className="w-3 h-3" /> Dupliquer
                      </Button>
                      <AddToItineraryButton kind="fertilisation" item={item} color="#8B5E3C" />
                    </div>
                  </div>
                  {deletingId === item.fertilisation_id ? (
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-[10px] font-medium" style={{ color: "#DC2626" }}>Supprimer ?</span>
                      <Button size="sm" variant="outline" className="h-6 text-[9px] px-2 rounded-lg border-red-200 text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(item.fertilisation_id)} data-testid="confirm-delete-btn">Oui</Button>
                      <Button size="sm" variant="ghost" className="h-6 w-6" onClick={() => setDeletingId(null)}>
                        <Trash2 className="w-3 h-3 rotate-45" />
                      </Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="ghost" className="h-7 w-7 text-red-400 hover:text-red-600 shrink-0"
                      onClick={() => setDeletingId(item.fertilisation_id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

function FertilisationForm({ parcelId, ilotId, refData, surfaceHa, cultureType, initial, onSaved, onCancel }) {
  const initCategory = initial?.category || "engrais";
  const known = fertiProducts(refData, initCategory).some(a => a.name === initial?.product_name);
  const [category, setCategory] = useState(initCategory);
  const [productName, setProductName] = useState(initial ? (known ? initial.product_name : "Autre") : "");
  const [customProduct, setCustomProduct] = useState(initial && !known ? (initial.product_name || "") : "");
  // Anciennes saisies « 3.0 L/ha » : dose et unité séparées
  const initialDose = splitDose(initial?.dose);
  const [dose, setDose] = useState(initialDose ? initialDose.value : (initial?.dose ?? ""));
  const [unit, setUnit] = useState((initialDose && initialDose.unit) || initial?.unit || "kg/ha");
  const [method, setMethod] = useState(initial?.method || "");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [surfaceApplied, setSurfaceApplied] = useState(surfaceHa?.toString() || "");
  const [operateur, setOperateur] = useState(initial?.operateur || "");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  // Teneurs saisies (produit libre ou de « mes produits ») : chaînes, une case vide n'est pas comptée
  const toInputs = (t) => t ? { ...Object.fromEntries(Object.entries(t).filter(([k]) => k !== "oligo").map(([k, v]) => [k, String(v)])),
    oligo: Object.fromEntries(Object.entries(t.oligo || {}).map(([k, v]) => [k, String(v)])) } : {};
  const [teneurs, setTeneurs] = useState(() => (initial && !known && initial.composition?.teneurs) ? toInputs(initial.composition.teneurs) : {});
  const [saveProduct, setSaveProduct] = useState(true);
  const autoNPK = useRef(null); // dernières teneurs tirées du nom, remplacées tant que l'utilisateur n'y a pas touché
  const [mesProduits, setMesProduits] = useState([]);

  useEffect(() => {
    authFetch(`${API_BASE}/carnet/mes-produits`).then(r => r.ok ? r.json() : []).then(setMesProduits).catch(() => {});
  }, []);

  const cultureOf = useCultureLookup();
  const products = useMemo(() => [
    ...mesProduits.filter(p => p.category === category).map(p => ({ ...p, mine: true })),
    ...fertiProducts(refData, category),
  ], [refData, category, mesProduits]);
  const productItems = useMemo(() => buildProductItems(products), [products]);
  const selectedProduct = products.find(p => p.name === productName);
  // Mon produit si je l'ai complété (teneurs ou densité), sinon le produit du catalogue
  const mineFor = (name) => mesProduits.find(m => m.name === name);
  const mineOfSelected = selectedProduct ? mineFor(selectedProduct.name) : null;
  // Teneurs à saisir : produit libre, « mon produit », ou produit de la liste sans composition connue (ex. chaux)
  const showTeneurs = productName === "Autre" || !!selectedProduct?.mine || (!!selectedProduct && !selectedProduct.formule && !mineOfSelected?.formule);
  // Densité : dose en litres et densité inconnue du catalogue comme de « mes produits »
  const showDensite = !!productName && isVolumeUnit(unit) && !selectedProduct?.densite && !mineOfSelected?.densite;
  const [densite, setDensite] = useState("");
  const [bouillie, setBouillie] = useState(initial?.volume_bouillie_l_ha ? String(initial.volume_bouillie_l_ha) : "");
  // En quittant un produit E-Phy, sa dose et son unité préremplies ne doivent pas rester sur l'apport suivant
  const clearEphyPrefill = () => { if (isEphy(selectedProduct)) { setDose(""); setUnit("kg/ha"); } };

  const handleProductSelect = (v) => {
    clearEphyPrefill();
    setProductName(v);
    if (v !== "Autre") setCustomProduct("");
    const p = products.find(x => x.name === v);
    setTeneurs(p?.mine ? toInputs(p.teneurs) : {});
    if (isEphy(p)) {
      // Dose E-Phy pour la culture : le nombre dans « Dose », l'unité dans « Unité » ; rien sans dose ou sans unité
      const d = splitDose(pickRecommendedDose(p, cultureType, cultureOf(cultureType)).raw);
      if (d && d.unit) { setDose(d.value); setUnit(d.unit); }
      return;
    }
    // Produit générique : pas de dose proposée, le producteur saisit la sienne
    if (p?.unit) setUnit(p.unit);
  };

  const handleSubmit = async () => {
    if (!productName) { toast.error("Selectionnez un produit"); return; }
    if (productName === "Autre" && !customProduct.trim()) { toast.error("Precisez le produit"); return; }
    setSaving(true);
    try {
      const res = await authFetch(`${API_BASE}/carnet/fertilisations`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          parcel_id: parcelId, ilot_id: ilotId || null, date, category, product_name: productName,
          custom_product: productName === "Autre" ? customProduct : null,
          dose, unit, method, surface_applied: surfaceApplied ? parseFloat(surfaceApplied) : null,
          operateur, notes,
          teneurs: showTeneurs ? teneurs : null, save_product: (showTeneurs || (showDensite && !!densite)) && saveProduct,
          densite: showDensite && densite ? parseFloat(String(densite).replace(",", ".")) : null,
          volume_bouillie_l_ha: isConcUnit(unit) && bouillie ? parseFloat(String(bouillie).replace(",", ".")) : null,
        }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      toast.success("Apport enregistre");
      onSaved();
    } catch (e) { toast.error(e.message || "Erreur"); }
    setSaving(false);
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-3" data-testid="fert-form">
        <div className="flex items-center gap-2 pb-2 border-b border-black/5">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onCancel}><ArrowLeft className="w-4 h-4" /></Button>
          <h3 className="text-sm font-bold" style={{ color: "#1A1A1A" }}>{initial ? "Dupliquer l'apport" : "Nouvel apport"}</h3>
        </div>

        <Field label="Date" value={date} onChange={setDate} type="date" required testId="fert-date" />

        <div>
          <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>Categorie *</Label>
          <div className="flex gap-2 mt-1">
            {[["amendement", "Amendement"], ["engrais", "Engrais"]].map(([val, label]) => (
              <Button key={val} variant={category === val ? "default" : "outline"} size="sm"
                className={`flex-1 h-9 text-xs rounded-lg ${category === val ? "text-white" : ""}`}
                style={category === val ? { background: "#8B5E3C" } : {}}
                onClick={() => { setCategory(val); setProductName(""); clearEphyPrefill(); }} data-testid={`fert-cat-${val}`}>
                {label}
              </Button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>Produit *</Label>
          <SearchableSelect
            items={productItems}
            value={productName}
            onSelect={handleProductSelect}
            placeholder="Rechercher un produit..."
            searchPlaceholder="Nom du produit ou n° AMM..."
            testId="fert-product-select"
            className="mt-1 text-sm"
          />
        </div>

        {selectedProduct && !selectedProduct.mine && <ProductInfoPanel product={selectedProduct} cultureType={cultureType} surfaceHa={surfaceHa} showDose={isEphy(selectedProduct)} />}

        <Field label="Produit absent de la liste ? Saisissez son nom" value={customProduct}
          onChange={(v) => {
            setCustomProduct(v);
            if (v.trim()) {
              clearEphyPrefill(); setProductName("Autre");
              // Formule dans le nom (« 15-12-24 ») : N, P2O5 et K2O pré-remplis tant qu'aucune teneur n'est saisie
              const f = teneursFromName(v);
              const prev = autoNPK.current;
              const untouched = ["N", "P2O5", "K2O"].every(k => (teneurs[k] || "") === ((prev && prev[k]) || ""));
              if (f && untouched) { setTeneurs(t => ({ ...t, ...f })); autoNPK.current = f; }
            } else if (productName === "Autre") setProductName("");
          }}
          placeholder="Nom du produit, formule (ex. 15-12-24)…" testId="fert-custom-product" />

        {showTeneurs && selectedProduct && !selectedProduct.mine && (
          <p className="text-[10px]" style={{ color: "#9A6A12" }}>Ce produit n'a pas de composition connue : recopiez ses teneurs depuis l'emballage pour qu'il compte dans le bilan.</p>
        )}
        {showTeneurs && <TeneursFields teneurs={teneurs} onChange={setTeneurs} />}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Dose" type="number" value={dose} onChange={setDose} placeholder="Ex: 300" testId="fert-dose" />
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>Unite</Label>
            <Select value={unit} onValueChange={setUnit}>
              <SelectTrigger className="h-9 text-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="z-[9999]">
                {[...new Set([...UNITS, unit].filter(Boolean))].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {isConcUnit(unit) && <BouillieField value={bouillie} onChange={setBouillie} unit={unit} />}
        {showDensite && <DensiteField value={densite} onChange={setDensite} />}
        {(showTeneurs || showDensite) && (
          <label className="flex items-center gap-2 text-xs" style={{ color: "#4A4A4A" }}>
            <input type="checkbox" checked={saveProduct} onChange={(e) => setSaveProduct(e.target.checked)} data-testid="fert-save-product" />
            {selectedProduct?.mine ? "Mettre à jour ce produit dans mes produits" : "Enregistrer dans mes produits pour les prochains apports"}
          </label>
        )}

        <div>
          <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>Methode d'application</Label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger className="h-9 text-sm mt-1"><SelectValue placeholder="Selectionnez..." /></SelectTrigger>
            <SelectContent className="z-[9999]">
              {(refData?.methodes_application || []).map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Surface (ha)" value={surfaceApplied} onChange={setSurfaceApplied} type="number" testId="fert-surface" />
          <Field label="Operateur" value={operateur} onChange={setOperateur} testId="fert-operateur" />
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>Notes</Label>
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Observations..." className="text-sm bg-white border-black/10 min-h-[50px] mt-1" data-testid="fert-notes" />
        </div>

        <Button onClick={handleSubmit} disabled={saving}
          className="w-full h-10 text-sm font-semibold rounded-xl text-white gap-2" style={{ background: "#8B5E3C" }}
          data-testid="fert-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Enregistrer
        </Button>
      </div>
    </ScrollArea>
  );
}

